import {
  apiDateToTimestamp,
  momentFromApiDate,
  momentToApiDate,
  timestampToApiDate,
} from 'Utils/date/apiDate';
import graphqlClient from 'Utils/graphql-client/graphqlClient';
import moment from 'moment';
import { getBookingPrecision } from '@clubmed/domains-service';

const getBookmarksQuery = (appContext) => `
      id
      productId
      resortDepartureDate
      resortArrivalDate
      attendeesCount
      adultsCount
      childrenCount
      birthdates
      departureOptionId
      product {
        accommodations {
          category
          categoryId
          comfortId
          comfortType
          id
          roomType
        }
        badgeIcon
        color
        comfortLabel
        destination {
          country
        }
        hasECSpace
        id
        image
        images
        isExplicitEC
        name
        type
        href
      }
      price {
        amount
        currency
        formattedValue(precision:${getBookingPrecision(appContext.locale)})
      }
  `;

const mapBookmarkEntity = ({
  adultsCount,
  birthdates,
  resortArrivalDate,
  resortDepartureDate,
  departureOptionId,
  price,
  ...rest
}) => {
  return {
    adultsNumber: adultsCount,
    ...(birthdates && {
      kids: birthdates.map((b) => ({
        birthdate: apiDateToTimestamp(b),
      })),
    }),
    departureDate: apiDateToTimestamp(resortDepartureDate),
    arrivalDate: apiDateToTimestamp(resortArrivalDate),
    departureCity: departureOptionId,
    ...(price && {
      priceOfStay: {
        bestPrice: {
          value: price.amount,
          formattedValue: price.formattedValue,
        },
      },
    }),
    duration: momentFromApiDate(resortArrivalDate).diff(
      momentFromApiDate(resortDepartureDate),
      'days',
    ),
    origin: 'BOOKMARK',
    ...rest,
  };
};

const mapSearchEntity = ({
  adults,
  birthdates,
  returnDate,
  departureDate,
  departureCity,
  isBookable,
  price,
  reference,
  resort,
  type,
  upsells,
}) => ({
  adultsNumber: adults,
  id: reference,
  kids: (birthdates || []).map((b) => ({
    birthdate: apiDateToTimestamp(b),
  })),
  departureDate: apiDateToTimestamp(departureDate),
  arrivalDate: apiDateToTimestamp(returnDate),
  departureCity,
  ...(price && {
    priceOfStay: {
      bestPrice: {
        value: price.value,
        formattedValue: price.formattedValue,
      },
    },
  }),
  isBookable,
  origin: 'SEARCH',
  product: resort,
  duration: momentFromApiDate(returnDate).diff(momentFromApiDate(departureDate), 'days'),
  reference,
  type,
  upsells,
});

const favoriteQueryParams = (favoriteProduct) => {
  const birthdates = favoriteProduct.kids.length
    ? `["${favoriteProduct.kids.map((kid) => timestampToApiDate(kid.birthdate)).join('","')}"]`
    : '[]';

  return `
  productId: "${favoriteProduct.product.id}",
  resortArrivalDate: "${momentToApiDate(
    moment(favoriteProduct.departureDate).add(favoriteProduct.duration, 'days'),
  )}",
  resortDepartureDate: "${timestampToApiDate(favoriteProduct.departureDate)}",
  childrenCount: ${favoriteProduct.kids.length},
  adultsCount: ${favoriteProduct.adultsNumber},
  birthdates: ${birthdates},
  departureOptionId: "${favoriteProduct.departureCity}"
  price: {
      value: ${favoriteProduct.priceOfStay.bestPrice.value}
      currency: "${favoriteProduct.priceOfStay.currency}"
  },
  `;
};

const addBookmarkMutation = (favoriteProduct, appContext) => {
  const gqlQuery = `mutation RootMutationType {
    addBookmark(${favoriteQueryParams(favoriteProduct)}) {
        ${getBookmarksQuery(appContext)}
      }
    }`;

  return graphqlClient.runNoCacheQuery(gqlQuery, appContext, {
    requestName: 'CUSTOMER_ACCOUNT_ADD_FAVORITE',
  });
};

const deleteBookmarkMutation = (id, appContext) => {
  const { cmsContent, cmsRelease } = appContext;

  const gqlQuery = `mutation RootMutationType {
    deleteBookmark(id: "${id}") {
        errorCode
        errorDescription(content:"${cmsContent}" release:"${cmsRelease}")
        errorKey
      }
    }`;

  return graphqlClient.runNoCacheQuery(gqlQuery, appContext, {
    requestName: 'CUSTOMER_ACCOUNT_DELETE_FAVORITE',
  });
};

const deleteCustomerProposalMutation = (proposalId, appContext) => {
  const { cmsContent, cmsRelease } = appContext;

  const gqlQuery = `mutation RootMutationType {
    deleteCustomerProposal(proposalId: "${proposalId}") {
        errorCode
        errorDescription(content:"${cmsContent}" release:"${cmsRelease}")
        errorKey
      }
    }`;

  return graphqlClient.runNoCacheQuery(gqlQuery, appContext, {
    requestName: 'CUSTOMER_ACCOUNT_DELETE_PROPOSAL',
  });
};

export {
  getBookmarksQuery,
  mapBookmarkEntity,
  mapSearchEntity,
  addBookmarkMutation,
  deleteBookmarkMutation,
  deleteCustomerProposalMutation,
};
